// Liste des pays francophones (codes ISO 3166-1 alpha-2)
export const FRANCOPHONE_COUNTRIES = [
  // Europe
  'FR', 'BE', 'CH', 'LU', 'MC',
  // Amérique du Nord
  'CA',
  // Afrique du Nord
  'DZ', 'MA', 'TN',
  // Afrique de l'Ouest
  'SN', 'CI', 'ML', 'BF', 'NE', 'BJ', 'TG', 'GN', 'GW', 'MR',
  // Afrique Centrale
  'CM', 'TD', 'CF', 'GA', 'CG', 'CD', 'GQ',
  // Afrique de l'Est
  'MG', 'RW', 'BI', 'DJ', 'KM', 'SC',
  // Océanie
  'VU', 'NC', 'PF', 'WF',
  // Territoires français d'outre-mer
  'PM', 'BL', 'MF', 'GF', 'GP', 'MQ', 'RE', 'YT', 'TF'
];

/**
 * Détermine la langue appropriée basée sur le code pays
 * @param countryCode Code pays ISO 3166-1 alpha-2
 * @returns 'fr' pour les pays francophones, 'en' pour les autres
 */
export const getLanguageFromCountry = (countryCode: string): 'fr' | 'en' => {
  return FRANCOPHONE_COUNTRIES.includes(countryCode.toUpperCase()) ? 'fr' : 'en';
};

/**
 * Services de géolocalisation IP disponibles
 */
const GEOLOCATION_SERVICES = [
  {
    name: 'ipapi.co',
    url: 'https://ipapi.co/country_code/',
    headers: { 'Accept': 'text/plain' }
  },
  {
    name: 'ipinfo.io', 
    url: 'https://ipinfo.io/country',
    headers: { 'Accept': 'text/plain' }
  }
];

/**
 * Détecte le pays de l'utilisateur basé sur son adresse IP
 * @returns Code pays ISO ou fallback basé sur la langue du navigateur
 */
export const detectCountryFromIP = async (): Promise<string> => {
  // Essayer chaque service de géolocalisation
  for (const service of GEOLOCATION_SERVICES) {
    try {
      const response = await fetch(service.url, {
        method: 'GET',
        headers: service.headers,
        // Timeout de 5 secondes
        signal: AbortSignal.timeout(5000)
      });
      
      if (response.ok) {
        const countryCode = await response.text();
        const cleanCode = countryCode.trim().toUpperCase();
        
        if (cleanCode && cleanCode.length === 2) {
          console.info(`Pays détecté via ${service.name}:`, cleanCode);
          return cleanCode;
        }
      }
    } catch (error) {
      console.warn(`Erreur avec ${service.name}:`, error);
    }
  }
  
  // Fallback final sur la langue du navigateur
  const browserLang = navigator.language.split('-')[0];
  const fallbackCountry = browserLang === 'fr' ? 'FR' : 'US';
  
  console.info('Utilisation du fallback basé sur le navigateur:', fallbackCountry);
  return fallbackCountry;
};

/**
 * Vérifie si l'utilisateur a déjà un choix de langue sauvegardé
 * @returns Langue sauvegardée ou null
 */
export const getSavedLanguage = (): 'fr' | 'en' | null => {
  try {
    const saved = localStorage.getItem('i18nextLng');
    return saved && ['fr', 'en'].includes(saved) ? (saved as 'fr' | 'en') : null;
  } catch {
    return null;
  }
};

/**
 * Sauvegarde le choix de langue de l'utilisateur
 * @param language Langue à sauvegarder
 */
export const saveLanguageChoice = (language: 'fr' | 'en'): void => {
  try {
    localStorage.setItem('i18nextLng', language);
    localStorage.setItem('languageDetectionMethod', 'geolocation');
    localStorage.setItem('languageDetectionDate', new Date().toISOString());
  } catch (error) {
    console.warn('Impossible de sauvegarder le choix de langue:', error);
  }
}; 